// Funciones auxiliares para arrays
export const unique = <T>(array: T[]): T[] => {
  return [...new Set(array)];
};

export const uniqueBy = <T, K>(array: T[], keyFn: (item: T) => K): T[] => {
  const seen = new Set<K>();
  return array.filter((item) => {
    const key = keyFn(item);
    if (seen.has(key)) {
      return false;
    }
    seen.add(key);
    return true;
  });
};

export const groupBy = <T, K extends string | number | symbol>(
  array: T[],
  keyFn: (item: T) => K,
): Record<K, T[]> => {
  return array.reduce(
    (groups, item) => {
      const key = keyFn(item);
      if (!groups[key]) {
        groups[key] = [];
      }
      groups[key].push(item);
      return groups;
    },
    {} as Record<K, T[]>,
  );
};

export const sortBy = <T>(
  array: T[],
  keyFn: (item: T) => string | number,
  direction: 'asc' | 'desc' = 'asc',
): T[] => {
  return [...array].sort((a, b) => {
    const aVal = keyFn(a);
    const bVal = keyFn(b);

    if (aVal < bVal) return direction === 'asc' ? -1 : 1;
    if (aVal > bVal) return direction === 'asc' ? 1 : -1;
    return 0;
  });
};

export const filterBy = <T>(array: T[], predicate: (item: T) => boolean): T[] => {
  return array.filter(predicate);
};

export const findById = <T extends { id: number | string }>(
  array: T[],
  id: number | string,
): T | undefined => {
  return array.find((item) => item.id === id);
};

export const findIndexById = <T extends { id: number | string }>(
  array: T[],
  id: number | string,
): number => {
  return array.findIndex((item) => item.id === id);
};

export const removeById = <T extends { id: number | string }>(
  array: T[],
  id: number | string,
): T[] => {
  return array.filter((item) => item.id !== id);
};

export const updateById = <T extends { id: number | string }>(
  array: T[],
  id: number | string,
  updates: Partial<T>,
): T[] => {
  return array.map((item) => (item.id === id ? { ...item, ...updates } : item));
};

export const chunk = <T>(array: T[], size: number): T[][] => {
  const chunks: T[][] = [];
  for (let i = 0; i < array.length; i += size) {
    chunks.push(array.slice(i, i + size));
  }
  return chunks;
};

export const flatten = <T>(array: T[][]): T[] => {
  return array.flat();
};

export const isEmpty = <T>(array: T[]): boolean => {
  return array.length === 0;
};

export const isArrayNotEmpty = <T>(array: T[]): boolean => {
  return array.length > 0;
};

export const first = <T>(array: T[]): T | undefined => {
  return array[0];
};

export const last = <T>(array: T[]): T | undefined => {
  return array[array.length - 1];
};

export const count = <T>(array: T[], predicate?: (item: T) => boolean): number => {
  if (!predicate) return array.length;
  return array.filter(predicate).length;
};

export const sum = <T>(array: T[], keyFn: (item: T) => number): number => {
  return array.reduce((total, item) => total + keyFn(item), 0);
};

export const average = <T>(array: T[], keyFn: (item: T) => number): number => {
  if (array.length === 0) return 0;
  return sum(array, keyFn) / array.length;
};

export const max = <T>(array: T[], keyFn: (item: T) => number): number => {
  if (array.length === 0) return 0;
  return Math.max(...array.map(keyFn));
};

export const min = <T>(array: T[], keyFn: (item: T) => number): number => {
  if (array.length === 0) return 0;
  return Math.min(...array.map(keyFn));
};
